/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { IntegrationAPI } from "../../../api/IntegrationAPI";
import { SettingsHelpVideosAtom } from "../../../atoms/SettingsHelpVideos";
import { UserAtom } from "../../../atoms/UserAtom";
import {
  ILearnisticIntegration,
  IStripeIntegration,
  IThrivecartIntegration,
} from "../../../generated/Api";
import { SettingsTab } from "../../../pages/settings";
import {
  HelpVideoNameMap,
  IHelpContexts,
} from "../../../types/HelpVideosTypes";
import StyledButton from "../../StyledButton";

const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  margin: 16px 0 16px;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
  }
`;

const IntegrationsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;

  margin-top: 64px;

  grid-gap: 64px;
`;

const IntegrationsCard = styled.div<{ isConnected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  > img {
    width: 100px;
    height: 100px;
    filter: ${(props) => (props.isConnected ? "grayscale(0)" : "grayscale(1)")};
  }
`;

interface IntegrationsProps {
  handleSelected?: (vle: SettingsTab) => void;
}

type GenericIntegration =
  | IThrivecartIntegration
  | ILearnisticIntegration
  | IStripeIntegration;

export const AllIntegrationsAtom = atom<GenericIntegration[]>({
  key: "AllIntegrationsAtom",
  default: [],
});

export default function Integrations({
  handleSelected,
}: IntegrationsProps): JSX.Element | null {
  const user = useRecoilValue(UserAtom);
  const [isConnected, setIsConnected] = useState(false);
  const [integrations, setIntegrations] =
    useRecoilState<GenericIntegration[]>(AllIntegrationsAtom);
  const [, setHelpVideo] = useRecoilState(SettingsHelpVideosAtom);

  useEffect(() => {
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.SettingsIntegrations],
      helpContext: IHelpContexts.SettingsIntegrations,
    });
  }, []);

  useEffect(() => {
    (async () => {
      if (!user) return;
      let integrations = await IntegrationAPI.getAll();
      if (integrations) setIntegrations(integrations);
    })();
  }, [user]);

  const thriveIntegration = integrations?.find((i) => i?.type === "THRIVECART");
  const learnisticIntegration = integrations?.find(
    (i) => i?.type === "LEARNISTIC"
  );
  const stripeIntegration = integrations?.find((i) => i?.type === "STRIPE");

  const hasStripeIntegrationAccess =
    user?.userUid === "oKpn2AGWLFPUC45pzuivmdlJroF2" ||
    user?.userUid === "ZHYBlalzKgeyNyXrb9Qakrfho2D3";

  return (
    <Container>
      <TextWrapper>
        <MainTitle>Integrations</MainTitle>
        <MainText>
          Stream-line your business by connecting BerserkerMail to your other
          accounts. Many more integrations coming post launch!
        </MainText>
      </TextWrapper>

      <IntegrationsWrapper>
        <IntegrationsCard isConnected={!!learnisticIntegration}>
          <img src="./images/learnistic.png" alt="learnistic" />

          <SecondaryTitle>Learnistic</SecondaryTitle>

          <StyledButton
            onClick={() => {
              handleSelected(SettingsTab.LEARNISTIC);
              setHelpVideo({
                helpTitle:
                  HelpVideoNameMap[IHelpContexts.SettingsLearnisticIntegration],
                helpContext: IHelpContexts.SettingsLearnisticIntegration,
              });
            }}
            css={css`
              width: 150px;
              ${learnisticIntegration
                ? ""
                : `
                  border-color: #fb9723;
                  color: #fb9723;
                `}
            `}
            inverted={!learnisticIntegration}
          >
            {learnisticIntegration ? "Configure" : "Connect"}
          </StyledButton>
        </IntegrationsCard>

        {hasStripeIntegrationAccess ? (
          <IntegrationsCard isConnected={!!stripeIntegration}>
            <img src="./images/stripe.png" alt="stripe" />

            <SecondaryTitle>Stripe</SecondaryTitle>

            <StyledButton
              onClick={() => {
                if (stripeIntegration) {
                  handleSelected(SettingsTab.STRIPEPRODUCTS);
                } else {
                  handleSelected(SettingsTab.STRIPE);
                }

                setHelpVideo({
                  helpTitle:
                    HelpVideoNameMap[
                      IHelpContexts.SettingsThrivecartIntegration
                    ],
                  helpContext: IHelpContexts.SettingsThrivecartIntegration,
                });
              }}
              css={css`
                width: 150px;
                ${stripeIntegration
                  ? ""
                  : `
                  border-color: #fb9723;
                  color: #fb9723;
                `}
              `}
              inverted={!stripeIntegration}
            >
              {stripeIntegration ? "Configure" : "Connect"}
            </StyledButton>
          </IntegrationsCard>
        ) : (
          <IntegrationsCard isConnected={isConnected}>
            <img src="./images/stripe.png" alt="stripe" />

            <SecondaryTitle>Stripe</SecondaryTitle>

            <StyledButton
              disable
              onClick={() => handleSelected(SettingsTab.STRIPE)}
              css={css`
                width: 150px;
                background: ${isConnected ? "#333333" : "transparent"};
                border-color: ${isConnected
                  ? "#333333"
                  : "rgba(0, 0, 0, 0.32)"};
                color: ${isConnected ? "white" : "#222"};
              `}
            >
              {isConnected ? "Configure" : "Coming Soon"}
            </StyledButton>
          </IntegrationsCard>
        )}

        <IntegrationsCard isConnected={!!thriveIntegration}>
          <img src="./images/thrivecart.png" alt="thrivecart" />

          <SecondaryTitle>Thrivecart</SecondaryTitle>

          <StyledButton
            onClick={() => {
              if (thriveIntegration) {
                handleSelected(SettingsTab.THRIVECARTPRODUCTS);
              } else {
                handleSelected(SettingsTab.THRIVECART);
              }
              setHelpVideo({
                helpTitle:
                  HelpVideoNameMap[IHelpContexts.SettingsThrivecartIntegration],
                helpContext: IHelpContexts.SettingsThrivecartIntegration,
              });
            }}
            css={css`
              width: 150px;
              ${thriveIntegration
                ? ""
                : `
                  border-color: #fb9723;
                  color: #fb9723;
                `}
            `}
            inverted={!thriveIntegration}
          >
            {thriveIntegration ? "Configure" : "Connect"}
          </StyledButton>
        </IntegrationsCard>
      </IntegrationsWrapper>
    </Container>
  );
}
